import { zodResolver } from '@hookform/resolvers/zod';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { ButtonGroup } from '@optools/components/ui/button-group';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@optools/components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { Separator } from '@optools/components/ui/separator';
import { Search, XCircle } from 'lucide-react';
import { ReactNode, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { searchFilterFormSchema, SearchFilterFormValues } from '../../schemas/filter';

export interface AuditTableFilterProps {
  defaultOpen?: boolean;
  icon: ReactNode;
  onChange: (value: string) => void;
  onClose?: () => void;
  title: string;
  value: string;
}

export function AuditTableFilter({
  defaultOpen = false,
  icon,
  onChange,
  onClose,
  title,
  value,
}: AuditTableFilterProps) {
  const [open, setOpen] = useState(defaultOpen);

  const handleSubmit = (value: string) => {
    onChange(value);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen === false) onClose?.();
      }}>
      <ButtonGroup className="**:text-xs">
        {value && (
          <Button size="icon-sm" variant="outline" onClick={() => onChange('')}>
            <XCircle />
            <span className="sr-only">Clear {title}</span>
          </Button>
        )}
        <PopoverTrigger asChild>
          <Button aria-label={title} className="transition-colors" size="sm" variant="outline">
            {icon}
            {title}
            {value && (
              <div className="-mr-1.5 flex gap-1">
                <Separator orientation="vertical" />
                <Badge className="rounded-sm" variant="secondary">
                  {value}
                </Badge>
              </div>
            )}
          </Button>
        </PopoverTrigger>
      </ButtonGroup>
      <PopoverContent align="start" alignOffset={value ? -32 : 0} className="w-full **:text-xs">
        <FilterForm title={title} value={value} onSubmit={handleSubmit} />
      </PopoverContent>
    </Popover>
  );
}

const FilterForm = ({
  onSubmit,
  title,
  value,
}: {
  title: string;
  value: string;
  onSubmit: (value: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const form = useForm<SearchFilterFormValues>({
    defaultValues: { value },
    resolver: zodResolver(searchFilterFormSchema),
  });

  const handleSubmit = ({ value }: SearchFilterFormValues) => {
    onSubmit(value);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{title}</FormLabel>
              <InputGroup>
                <FormControl>
                  <InputGroupInput
                    {...field}
                    ref={(node) => {
                      node?.focus();
                      inputRef.current = node;
                    }}
                    className="field-sizing-content min-w-32 p-1.5 text-xs! placeholder:text-xs focus-visible:ring-0"
                  />
                </FormControl>
                <InputGroupAddon align="inline-end">
                  <InputGroupButton size="icon-xs" type="submit" variant="link">
                    <Search /> <span className="sr-only">Search</span>
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
